/**
 * The single place user-visible text lives.
 *
 * Section 14 requires that the interface language is English initially "with all
 * labels in localization resources". Nothing in a screen may hard-code a string,
 * so adding a second language later is a new resource map here rather than a
 * sweep through every component. Keys are grouped by screen and never reused
 * across screens, because the same English word often needs different
 * translations in different places.
 */

export const en = {
  'app.name': 'Chat',
  'app.loading': 'Loading',
  'app.restoring': 'Restoring your session',

  'nav.inbox': 'Conversations',
  'nav.settings': 'Settings',
  'nav.back': 'Back',
  'nav.conversation': 'Conversation',

  // CR-01 §4: four ordinary statuses plus actionable exception states. The
  // trailing ellipsis is part of the specified wording for the three transient
  // ones; Connected has none because it is a resting state, not a progress one.
  'connection.waitingForNetwork': 'Waiting for network…',
  'connection.waitingForNetwork.detail': 'Messages are saved and will send when connected.',
  'connection.connecting': 'Connecting…',
  'connection.connecting.detail': 'Trying to reach the server.',
  'connection.connecting.retryIn': 'Retrying in {seconds}s',
  'connection.updating': 'Updating…',
  'connection.updating.detail': 'Getting your latest messages.',
  'connection.updating.delayed': 'Updates are delayed. Retrying automatically.',
  'connection.connected': 'Connected',
  'connection.connected.slow': 'Slow connection',
  'connection.signIn': 'Sign in again',
  'connection.signIn.detail': 'Your session ended. Anything you have written is still saved.',
  'connection.updateClient': 'Update required',
  'connection.updateClient.detail': 'This version can no longer talk to the server.',
  'connection.certificateFailure': 'Connection could not be verified',
  'connection.certificateFailure.detail': 'A trusted connection to the server could not be established.',
  'connection.refreshingChannels': 'Refreshing channels…',
  'connection.loadingMessages': 'Loading messages…',
  'connection.retryNow': 'Retry now',
  'connection.pendingCount': '{count} waiting to send',
  'connection.status': 'Connection status',

  'storage.reduced': 'Offline storage is limited on this device. Messages may not be kept after you close this tab.',
  'storage.unavailable': 'This browser is not storing data for this site. Offline history and queued messages are unavailable.',

  'signin.title': 'Sign in',
  'signin.subtitle': 'Enter your phone number and we will send you a verification code.',
  'signin.country': 'Country',
  'signin.phone': 'Phone number',
  'signin.phonePlaceholder': 'Phone number',
  'signin.sendCode': 'Send code',
  'signin.sendingCode': 'Sending code',
  'signin.codeTitle': 'Enter the code',
  'signin.codeSubtitle': 'We sent a code to {phone}.',
  'signin.code': 'Verification code',
  'signin.codePlaceholder': '123456',
  'signin.verify': 'Sign in',
  'signin.verifying': 'Signing in',
  'signin.resendIn': 'You can request another code in {seconds}s',
  'signin.resend': 'Send another code',
  'signin.changeNumber': 'Use a different number',
  'signin.errorGeneric': 'That did not work. Check the details and try again.',
  'signin.errorRateLimited': 'Too many attempts. Try again in {seconds}s.',
  'signin.errorNetwork': 'Could not reach the server. Check your connection and try again.',
  'signin.errorPhone': 'Enter a phone number.',
  'signin.errorCode': 'Enter the code you received.',

  'inbox.title': 'Chat',
  'inbox.search': 'Search conversations',
  'inbox.searchPlaceholder': 'Search',
  'inbox.newConversation': 'New conversation',
  'inbox.empty': 'No conversations yet.',
  'inbox.emptySearch': 'No conversations match that search.',
  'inbox.draftPrefix': 'Draft:',
  'inbox.unread': '{count} unread',
  'inbox.unreadCapped': 'Many unread',
  'inbox.selectPrompt': 'Select a conversation.',
  'inbox.openConversation': 'Open conversation with {title}',
  'inbox.noMessages': 'No messages yet',

  'newConversation.title': 'New conversation',
  'newConversation.description': 'Find someone by username or phone number.',
  'newConversation.query': 'Username or phone number',
  'newConversation.find': 'Find',
  'newConversation.finding': 'Searching',
  'newConversation.start': 'Start conversation',
  'newConversation.notFound': 'No account matched that. Check the spelling and try again.',
  'newConversation.offline': 'You need a connection to start a new conversation.',
  'newConversation.cancel': 'Cancel',

  'conversation.timeline': 'Message timeline',
  'conversation.empty': 'No messages yet. Say hello.',
  'conversation.loadingOlder': 'Loading earlier messages',
  'conversation.jumpToLatest': 'Jump to latest',
  'conversation.newMessages': 'New messages',
  'conversation.historyStart': 'This is the start of the conversation.',
  'conversation.notFound': 'That conversation is not available on this device.',
  'conversation.unknownSender': 'Unknown',
  'conversation.you': 'You',
  'conversation.typing': '{names} is typing…',
  'presence.online': 'online',
  'presence.lastSeen': 'last seen {when}',

  'composer.placeholder': 'Write a message',
  'composer.label': 'Message',
  'composer.send': 'Send',
  'composer.hint': 'Press Enter to send, Shift and Enter for a new line.',
  'composer.replyingTo': 'Replying to {name}',
  'composer.cancelReply': 'Remove reply',
  'composer.editing': 'Editing a message',
  'composer.cancelEdit': 'Cancel edit',
  'composer.saveEdit': 'Save',

  'message.actions': 'Message actions',
  'message.reply': 'Reply',
  'message.copy': 'Copy text',
  'message.copied': 'Copied',
  'message.copyFailed': 'Could not copy on this device.',
  'message.edit': 'Edit',
  'message.delete': 'Delete',
  'message.close': 'Close menu',
  'message.edited': 'edited',
  // The three delivery states. Spelled out for assistive technology, because a
  // tick glyph read aloud is meaningless.
  'message.sent': 'Sent',
  'message.delivered': 'Delivered',
  'message.read': 'Read',
  'message.deleted': 'This message was deleted.',
  'message.retry': 'Try again',
  'message.discard': 'Discard',
  'message.replyPreview': 'Reply to {name}',

  'delete.title': 'Delete message',
  'delete.description': 'Choose who this message is deleted for. This cannot be undone.',
  'delete.scopeSelf': 'Delete for me',
  'delete.scopeSelfHint': 'The message stays visible to everyone else.',
  'delete.scopeEveryone': 'Delete for everyone',
  'delete.scopeEveryoneHint': 'The message is removed for every participant.',
  'delete.confirm': 'Delete',
  'delete.cancel': 'Cancel',

  'status.label': 'Delivery status',
  'status.queued': 'Queued on this device',
  'status.sending': 'Sending',
  'status.committed': 'Sent',
  'status.delivered': 'Delivered',
  'status.read': 'Read',
  'status.failed': 'Not sent',
  'status.localFailure': 'Could not save message on this device',

  'settings.title': 'Settings',
  'settings.profile': 'Profile',
  'settings.profileHint': 'Name, username and photo.',
  'settings.sessions': 'Sessions',
  'settings.sessionsHint': 'Devices signed in to this account.',
  'settings.notifications': 'Notifications',
  'settings.notificationsHint': 'Alerts and push delivery.',
  'settings.privacy': 'Privacy',
  'settings.privacyHint': 'Who can see your details and reach you.',
  'settings.storage': 'Data and storage',
  'settings.storageHint': 'Cached history and media on this device.',
  'settings.account': 'Account',
  'settings.accountHint': 'Change number, export or delete the account.',
  'settings.pending': 'Not available yet.',
  'settings.signOut': 'Sign out',
  'settings.signOutPendingTitle': 'You have unsent messages',
  'settings.signOutPendingBody': 'Signing out now discards {count} message(s) that have not reached the server yet.',
  'settings.signOutConfirm': 'Sign out anyway',
  'settings.signOutCancel': 'Stay signed in',
  'settings.storageUsage': 'Using about {used} of {quota} available to this site.',
  'settings.storageUnknown': 'This browser does not report a storage estimate.',

  // --- Phase 2: attachments -------------------------------------------------
  'attach.menu': 'Attach',
  'attach.open': 'Add an attachment',
  'attach.photoVideo': 'Photo or video',
  'attach.file': 'File',
  'attach.recordVoice': 'Record voice message',
  'attach.recordVideo': 'Record video message',
  'attach.close': 'Close attachment menu',
  'attach.tooLarge': 'That file is larger than the 4 GiB limit.',
  'attach.tooMany': 'An album holds at most {count} items.',
  'attach.preparing': 'Preparing {name}',

  'upload.list': 'Attachments',
  'upload.progress': '{percent}% of {size} confirmed',
  'upload.waiting': 'Waiting to upload',
  'upload.uploading': 'Uploading',
  'upload.verifying': 'Checking on the server',
  'upload.ready': 'Ready to send',
  'upload.paused': 'Paused',
  'upload.failed': 'Upload failed',
  'upload.expired': 'This upload expired. Attach the file again.',
  'upload.pause': 'Pause upload',
  'upload.resume': 'Resume upload',
  'upload.cancel': 'Cancel upload',
  'upload.retry': 'Try upload again',
  'upload.remove': 'Remove attachment',
  'upload.notReady': 'Wait for attachments to finish uploading.',

  'media.download': 'Download',
  'media.downloading': 'Downloading',
  'media.downloadFailed': 'Could not download this file.',
  'media.open': 'Open',
  'media.unknownFormat': 'This file cannot be previewed here.',
  'media.notReady': 'This attachment is still being processed.',
  'media.failed': 'This attachment could not be processed. The original file is still available.',
  'media.size': '{size}',
  'media.confirmTitle': 'Download over a metered connection?',
  'media.confirmBody':
    '{name} is {size}. Your connection is metered or set to low data, so this download may cost you.',
  'media.confirmDownload': 'Download anyway',
  'media.confirmCancel': 'Not now',
  'media.protected': 'Saving is turned off in this conversation.',
  'media.photo': 'Photo',
  'media.video': 'Video',
  'media.voice': 'Voice message',
  'media.videoNote': 'Video message',
  'media.album': 'Album with {count} items',

  'record.start': 'Start recording',
  'record.stop': 'Stop recording',
  'record.recording': 'Recording',
  'record.elapsed': 'Recorded {duration}',
  'record.cancel': 'Discard recording',
  'record.review': 'Review before sending',
  'record.send': 'Send voice message',
  'record.denied': 'Microphone access was refused. You can still write a message.',
  'record.unsupported': 'This browser cannot record audio. You can still write a message.',
  'record.cameraDenied': 'Camera access was refused. You can still write a message.',
  'record.limit': 'A voice message can be up to 60 minutes.',
  'record.videoLimit': 'A video message can be up to 60 seconds.',

  'player.play': 'Play',
  'player.pause': 'Pause',
  'player.seek': 'Playback position',
  'player.speed': 'Playback speed',
  'player.speedValue': '{rate}x',
  'player.position': '{position} of {duration}',
  'player.waveform': 'Audio waveform',

  // --- Phase 2: reactions, forwarding, pins, drafts, search -----------------
  'reaction.add': 'Add a reaction',
  'reaction.remove': 'Remove your {emoji} reaction',
  'reaction.apply': 'React with {emoji}',
  'reaction.count': '{emoji}, {count}',
  'reaction.limit': 'You can add up to three reactions to a message.',
  'reaction.picker': 'Choose a reaction',

  'forward.action': 'Forward',
  'forward.title': 'Forward message',
  'forward.description': 'Choose where to send a copy.',
  'forward.search': 'Search conversations',
  'forward.send': 'Forward here',
  'forward.cancel': 'Cancel',
  'forward.protected': 'This conversation does not allow forwarding.',
  'forward.origin': 'Forwarded from {name}',
  'forward.originHidden': 'Forwarded',

  'pin.action': 'Pin',
  'pin.remove': 'Unpin',
  'pin.title': 'Pinned messages',
  'pin.open': 'Show pinned messages',
  'pin.empty': 'Nothing is pinned yet.',
  'pin.limit': 'A conversation can hold up to 100 pinned messages.',
  'pin.close': 'Close pinned messages',

  'draft.conflictTitle': 'This draft changed on another device',
  'draft.conflictBody': 'Choose which version to keep.',
  'draft.keepLocal': 'Keep what I typed here',
  'draft.keepRemote': 'Use the other version',
  'draft.otherVersion': 'Other device: {text}',

  'saved.title': 'Saved Messages',
  'saved.open': 'Open Saved Messages',
  'saved.unavailable': 'Saved Messages is not available on this device yet.',

  'search.inConversation': 'Search in this conversation',
  'search.open': 'Search this conversation',
  'search.close': 'Close search',
  'search.placeholder': 'Search messages',
  'search.run': 'Search',
  'search.results': 'Search results',
  'search.empty': 'No messages matched.',
  'search.loading': 'Searching',
  'search.unavailable': 'Search is unavailable right now. Messages still send and arrive.',
  'search.offline': 'Search needs a connection.',
  'search.tooLong': 'That search is too long.',
  'search.openResult': 'Open this message',
  'search.filterSender': 'From',
  'search.filterAnySender': 'Anyone',
  'search.filterMedia': 'Type',
  'search.filterAnyMedia': 'Any',
  'search.mediaPhoto': 'Photos',
  'search.mediaVideo': 'Videos',
  'search.mediaFile': 'Files',
  'search.mediaVoice': 'Voice',

  // --- Phase 3: groups ------------------------------------------------------
  'group.create': 'New group',
  'group.createTitle': 'Create a group',
  'group.name': 'Group name',
  'group.historyPolicy': 'New members can read',
  'group.historyAll': 'The whole history',
  'group.historySinceJoin': 'Messages sent after they join',
  'group.historyFixed': 'This cannot be changed later without an owner action.',
  'group.createAction': 'Create group',
  'group.cancel': 'Cancel',

  'group.info': 'Group info',
  'group.openInfo': 'Open group info',
  'group.members': 'Members',
  'group.memberCount': '{count} members',
  'group.yourRole': 'Your role: {role}',
  'group.roleOwner': 'Owner',
  'group.roleAdmin': 'Administrator',
  'group.roleMember': 'Member',
  'group.roleRestricted': 'Restricted',
  'group.roleNone': 'Not a member',
  'group.banned': 'Banned',
  'group.makeAdmin': 'Make administrator',
  'group.removeAdmin': 'Remove administrator',
  'group.ban': 'Ban from group',
  'group.unban': 'Remove ban',
  'group.leave': 'Leave group',
  'group.leaveTitle': 'Leave this group?',
  'group.leaveBody': 'You will stop receiving its messages and it will be removed from your list.',
  'group.leaveConfirm': 'Leave',
  'group.ownerMustTransfer': 'Transfer ownership before you leave the group.',
  'group.transfer': 'Transfer ownership',
  'group.transferTo': 'Transfer ownership to {name}',
  'group.transferUnavailable': 'Transferring ownership needs a fresh sign-in.',

  'invite.title': 'Invite link',
  'invite.create': 'Create an invite link',
  'invite.maxUses': 'Maximum uses',
  'invite.expiresDays': 'Expires in days',
  'invite.approval': 'Require approval to join',
  'invite.copy': 'Copy link',
  'invite.copied': 'Copied',
  'invite.revoke': 'Revoke',
  'invite.none': 'No invite links yet.',
  'invite.expires': 'Expires {date}',
  'invite.join': 'Join with a link',
  'invite.joinTitle': 'Join a group or channel',
  'invite.joinDescription': 'Paste an invite link or type a public username.',
  'invite.joinField': 'Invite link or @username',
  'invite.joinAction': 'Join',
  'invite.joinPending': 'Your request to join was sent and is waiting for approval.',
  'invite.joinFailed': 'That link did not work. It may have expired or been used up.',

  'requests.title': 'Join requests',
  'requests.approve': 'Approve',
  'requests.reject': 'Reject',
  'requests.empty': 'No one is waiting to join.',

  'slowmode.title': 'Slow mode',
  'slowmode.off': 'Off',
  'slowmode.seconds': '{seconds} seconds',
  'slowmode.wait': 'Slow mode is on. You can send again in {seconds}s.',
  'slowmode.description': 'How long members wait between messages. Administrators are exempt.',

  'protected.title': 'Protected content',
  'protected.description':
    'Turns off forwarding, copying and saving in this conversation. It cannot stop someone photographing the screen.',

  'topic.title': 'Topics',
  'topic.all': 'All topics',
  'topic.create': 'New topic',
  'topic.name': 'Topic name',
  'topic.createAction': 'Create topic',
  'topic.close': 'Close topic',
  'topic.reopen': 'Reopen topic',
  'topic.delete': 'Delete topic',
  'topic.closed': 'This topic is closed. Administrators can reopen it.',
  'topic.empty': 'No topics yet.',
  'topic.select': 'Choose a topic',

  'mention.label': 'Mention a member',
  'mention.empty': 'No members match.',
  'mention.hint': 'Type @ to mention someone.',

  // --- Phase 3: polls and quizzes ------------------------------------------
  'poll.create': 'Poll',
  'poll.createTitle': 'Create a poll',
  'poll.question': 'Question',
  'poll.option': 'Option {number}',
  'poll.addOption': 'Add option',
  'poll.removeOption': 'Remove option {number}',
  'poll.multiple': 'Allow multiple answers',
  'poll.anonymous': 'Anonymous votes',
  'poll.quiz': 'Quiz mode',
  'poll.quizHint': 'A quiz has one correct answer and cannot be changed once answered.',
  'poll.correct': 'Mark option {number} as correct',
  'poll.explanation': 'Explanation shown after answering',
  'poll.closesAt': 'Closes after (hours)',
  'poll.createAction': 'Create poll',
  'poll.vote': 'Vote',
  'poll.voteFor': 'Vote for {option}',
  'poll.retract': 'Change answer',
  'poll.close': 'Close poll',
  'poll.closed': 'Final results',
  'poll.open': 'Open poll',
  'poll.votes': '{count} votes',
  'poll.anonymousNote': 'Anonymous poll. Only totals are shown.',
  'poll.quizCorrect': 'Correct answer',
  'poll.quizYourAnswer': 'Your answer',
  'poll.quizHidden': 'The correct answer appears after you answer or when the poll closes.',
  'poll.quizRight': 'That is correct.',
  'poll.quizWrong': 'That is not correct.',
  'poll.multipleNote': 'Choose one or more.',
  'poll.singleNote': 'Choose one.',

  // --- Phase 4: channels ----------------------------------------------------
  'channel.create': 'New channel',
  'channel.createTitle': 'Create a channel',
  'channel.name': 'Channel name',
  'channel.username': 'Public username (optional)',
  'channel.usernameHint': 'A channel with a public username can be found and read by anyone.',
  'channel.createAction': 'Create channel',
  'channel.info': 'Channel info',
  'channel.openInfo': 'Open channel info',
  'channel.public': 'Public channel',
  'channel.private': 'Private channel',
  'channel.subscribe': 'Subscribe',
  'channel.unsubscribe': 'Unsubscribe',
  'channel.subscribed': 'Subscribed',
  'channel.notifications': 'Notifications',
  'channel.notifyAll': 'Every post',
  'channel.notifyMentions': 'Mentions and replies only',
  'channel.notifyNone': 'Nothing',
  'channel.publish': 'Publish',
  'channel.publishSilently': 'Publish without a notification',
  'channel.silentHint': 'Subscribers still see the post; their devices stay quiet.',
  'channel.views': 'about {count} views',
  'channel.viewsHint': 'View counts are approximate and are not read receipts.',
  'channel.comments': 'Comments',
  'channel.openDiscussion': 'Open the discussion group',
  'channel.noDiscussion': 'This channel has no discussion group.',
  'channel.linkDiscussion': 'Link a discussion group',
  'channel.unlinkDiscussion': 'Unlink the discussion group',
  'channel.linkHint': 'You need administrator rights in both the channel and the group.',
  'channel.readOnly': 'Only administrators can post in this channel.',
} as const;

export type StringKey = keyof typeof en;

/** Values substituted into a `{placeholder}` in a resource string. */
export type StringParams = Readonly<Record<string, string | number>>;

/**
 * The BCP 47 tag used for date, time and number formatting.
 *
 * It is a resource rather than a call to `navigator.language` because the
 * formatting locale has to follow the interface language, not the browser's
 * preferred language: showing English labels beside Arabic-Indic digits would
 * be a worse result than either alone.
 */
export const localeTag = 'en';

/**
 * Writing direction of the interface language. Message *content* direction is
 * decided per message with `dir="auto"`, which is what Section 14 means by
 * supporting mixed-direction content independently of the UI language.
 */
export const localeDirection: 'ltr' | 'rtl' = 'ltr';

export function formatString(template: string, params?: StringParams): string {
  if (!params) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export function translate(key: StringKey, params?: StringParams): string {
  return formatString(en[key], params);
}
